import React, { useEffect, useRef, useState } from 'react';
import { Wand2, Loader2, X, ArrowLeft, Download, Brush, Type, Eraser, Undo2 } from 'lucide-react';
import { editImage, type EditMode } from '../../services/mediaEditService';
import { resolveDownloadUrl, fetchAsBlob, uploadToPath } from '../../services/storageService';
import { auth } from '../../firebase/config';
import { TopUpNotice } from '../wallet/TopUpCard';
import MaskCanvas, { type MaskCanvasHandle } from './MaskCanvas';
import { TYPE } from '../../constants/typeScale';

interface Props {
  /** The source image's storage path (an upload or a generated asset the caller owns). */
  sourcePath: string;
  /** A display URL for the source (already resolved by the lightbox). */
  sourceUrl: string;
  conversationId?: string | null;
  onClose: () => void;
}

/**
 * EditImagePanel — edit an existing image in two plain-English lanes:
 *   • "Describe a change" — type an instruction; the whole image regenerates with the edit applied (Kontext).
 *   • "Change an area"    — paint a region, describe what goes there; only that region regenerates (generative fill).
 * Charge-on-success; the result shows in place and can be edited again (chain edits) or downloaded. Four states.
 * Monochrome, Lucide, no model names. The craft (masks, models) stays hidden behind plain words.
 */
const EditImagePanel: React.FC<Props> = ({ sourcePath, sourceUrl, conversationId, onClose }) => {
  const [mode, setMode] = useState<EditMode>('describe');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needsTopUp, setNeedsTopUp] = useState(false);
  const [hasPaint, setHasPaint] = useState(false);
  const [brushSize, setBrushSize] = useState(28);
  const [erase, setErase] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  // The current image being shown/edited — starts as the source, becomes the edited result (so edits chain).
  const [current, setCurrent] = useState<{ path: string; url: string }>({ path: sourcePath, url: sourceUrl });

  // Escape closes the editor back to the viewer (captured first so the lightbox behind us doesn't also close).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  const maskRef = useRef<MaskCanvasHandle>(null);

  // Upload the painted mask to the caller's own attachments so the backend can read it (IDOR-scoped path).
  const uploadMask = async (): Promise<string | null> => {
    const uid = auth.currentUser?.uid; if (!uid) return null;
    const blob = await maskRef.current?.exportMask();
    if (!blob) return null;
    const path = `chat-attachments/${uid}/masks/${Date.now()}.png`;
    await uploadToPath(path, blob, 'image/png');
    return path;
  };

  const run = async () => {
    const p = prompt.trim();
    if (!p || busy) return;
    if (mode === 'area' && !hasPaint) { setError('Paint the area you want to change first.'); return; }
    setBusy(true); setError(''); setNeedsTopUp(false);
    try {
      const maskPath = mode === 'area' ? await uploadMask() : undefined;
      if (mode === 'area' && !maskPath) { setError('Could not read your selection — try painting again.'); return; }
      const res = await editImage({ sourcePath: current.path, prompt: p, mode, maskPath: maskPath || undefined, conversationId });
      if (res.needsTopUp) { setNeedsTopUp(true); return; }
      if (res.storageFull) { setError('You are out of storage — free some space or upgrade to keep editing.'); return; }
      if (res.providerDown || !res.path) { setError('Editing is temporarily unavailable — please try again in a moment.'); return; }
      const url = await resolveDownloadUrl(res.path);
      setCurrent({ path: res.path, url });   // chain: the next edit works from THIS result
      setPrompt(''); maskRef.current?.clear(); setHasPaint(false);
    } catch { setError('Could not edit the image. Please try again.'); }
    finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const blob = await fetchAsBlob(current.url);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = 'edited.png';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    } catch { /* best-effort */ }
  };

  const edited = current.path !== sourcePath;
  const placeholder = mode === 'area'
    ? 'What goes in the painted area? — e.g. a red vintage motorcycle'
    : 'Describe the change — e.g. make the sky a sunset, remove the person on the left';

  return (
    <div onClick={(e) => e.stopPropagation()} className="flex w-[92vw] max-w-lg flex-col gap-3 rounded-2xl border border-secondary-800 bg-secondary-900 p-4 text-white">
      <div className="flex items-center gap-2">
        <button onClick={onClose} aria-label="Back to viewer" className="rounded-full p-1.5 text-white/70 hover:bg-white/10 hover:text-white"><ArrowLeft className="h-4 w-4" /></button>
        <span className={`flex items-center gap-1.5 ${TYPE.sectionTitle}`}><Wand2 className="h-4 w-4" /> Edit image</span>
        {edited && <button onClick={download} title="Download the edited image" className={`ml-auto inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 ${TYPE.control} text-white/80 hover:bg-white/10`}><Download className="h-3.5 w-3.5" /> Download</button>}
        {!edited && <button onClick={onClose} aria-label="Close" className="ml-auto rounded-full p-1.5 text-white/70 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>}
      </div>

      {/* Lane toggle — describe the whole change, or paint an area to change */}
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-black/30 p-1">
        {([['describe', 'Describe a change', Type], ['area', 'Change an area', Brush]] as const).map(([m, label, Icon]) => (
          <button key={m} onClick={() => { setMode(m); setError(''); }}
            className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 ${TYPE.control} font-semibold ${mode === m ? 'bg-white text-secondary-900' : 'text-white/70 hover:text-white'}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-center overflow-hidden rounded-xl bg-black">
        {busy ? (
          <div className="flex aspect-square w-full max-w-sm items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-white/60" /></div>
        ) : mode === 'area' ? (
          <MaskCanvas ref={maskRef} imageUrl={current.url} brushSize={brushSize} erase={erase} onPaintChange={setHasPaint} onHistoryChange={setCanUndo} />
        ) : (
          <img src={current.url} alt={edited ? 'Edited image' : 'Image to edit'} className="max-h-[46dvh] w-full object-contain" />
        )}
      </div>

      {mode === 'area' && (
        <div className={`flex flex-wrap items-center gap-2 ${TYPE.meta} text-white/60`}>
          {/* Paint / Erase — add to or trim the selected region */}
          <div className="inline-flex rounded-md bg-black/30 p-0.5">
            {([[false, 'Paint', Brush], [true, 'Erase', Eraser]] as const).map(([val, label, Icon]) => (
              <button key={label} onClick={() => setErase(val)}
                className={`inline-flex items-center gap-1 rounded px-2 py-1 ${erase === val ? 'bg-white text-secondary-900' : 'text-white/70 hover:text-white'}`}>
                <Icon className="h-3 w-3" /> {label}
              </button>
            ))}
          </div>
          {/* Brush size */}
          <label className="inline-flex items-center gap-1.5" title="Brush size">
            <span className="rounded-full bg-white/70" style={{ width: 10, height: 10 }} />
            <input type="range" min={8} max={80} value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))}
              aria-label="Brush size" className="h-1 w-24 cursor-pointer accent-white" />
          </label>
          <button onClick={() => maskRef.current?.undo()} disabled={!canUndo}
            className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-white/70 hover:bg-white/10 disabled:opacity-40"><Undo2 className="h-3 w-3" /> Undo</button>
          <button onClick={() => { maskRef.current?.clear(); setHasPaint(false); }} disabled={!hasPaint}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-white/70 hover:bg-white/10 disabled:opacity-40"><X className="h-3 w-3" /> Clear</button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); run(); } }}
          rows={2}
          placeholder={placeholder}
          className={`min-w-0 flex-1 resize-none rounded-lg border border-white/15 bg-black/30 px-3 py-2 ${TYPE.body} text-white placeholder:text-white/40 focus:outline-none focus:border-white/40`}
        />
        <button
          onClick={run}
          disabled={busy || !prompt.trim() || (mode === 'area' && !hasPaint)}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white px-4 py-2.5 ${TYPE.sectionTitle} text-secondary-900 hover:bg-secondary-100 disabled:opacity-40`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          {edited ? 'Edit again' : 'Edit'}
        </button>
      </div>

      {needsTopUp && <TopUpNotice />}
      {error && <p className={`${TYPE.control} text-secondary-400`}>{error}</p>}
    </div>
  );
};

export default EditImagePanel;
